import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Pagination,
    CircularProgress,
    Alert,
    Grid,
    Button,
    Container
} from '@mui/material';
import {
    ArrowBack,
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import PageContainer from '../layout/PageContainer';
import LongPostCard from './LongPostCard';

const LongPosts = () => {
    const navigate = useNavigate();
    const [longPosts, setLongPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Mock topics for now
    const mockTopics = ['Technology', 'Science', 'History', 'Literature', 'Business', 'Health'];

    const [selectedTopics, setSelectedTopics] = useState([]);

    useEffect(() => {
        fetchLongPosts();
    }, [page]);

    const fetchLongPosts = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await userRequest.get('/contentLongPost', {
                params: { 
                    page, 
                    limit: 12
                }
            });
            
            setLongPosts(response.data.longPosts);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.error('Error fetching long posts:', error);
            setError('Failed to load long posts. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    if (loading && longPosts.length === 0) {
        return (
            <PageContainer>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    const handleTopicToggle = (topic) => {
        setSelectedTopics(prev => 
            prev.includes(topic) 
                ? prev.filter(t => t !== topic)
                : [...prev, topic]
        );
    };

    return (
        <PageContainer>
            <Container maxWidth="lg" sx={{ py: 4, backgroundColor: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/profile')}
                    >
                        Back
                    </Button>
                    <Typography variant="h4" fontWeight="600" sx={{ flex: 1, textAlign: 'center' }}>
                        Longread
                    </Typography>
                    <Box sx={{ width: 100 }} /> {/* Spacer for centering */}
                </Box>

                {/* Topic Buttons */}
                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                        }}
                    >
                        {mockTopics.map((topic) => (
                            <Button
                                key={topic}
                                variant={selectedTopics.includes(topic) ? 'contained' : 'outlined'}
                                onClick={() => handleTopicToggle(topic)}
                                sx={{
                                    borderRadius: '20px',
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    boxShadow: selectedTopics.includes(topic) ? 3 : 0,
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {topic}
                            </Button>
                        ))}
                    </Box>
                </Box>
                
                <Box sx={{ mb: 4 }}>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={4}>
                    {longPosts.map((post) => (
                        <Grid item xs={12} sm={6} md={4} key={post._id}>
                            <LongPostCard post={post} />
                        </Grid>
                    ))}
                </Grid>

                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(event, newPage) => setPage(newPage)}
                            color="primary"
                        />
                    </Box>
                )}
                </Box>
            </Container>
        </PageContainer>
    );
};

export default LongPosts; 